import { createContext, use, useState, type ReactNode } from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  gridRowSelectionIdsSelector,
  useGridApiContext,
  useGridSelector,
  type DataGridPremiumProps,
  type GridColDef,
  type GridRowSelectionModel,
  type GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { Checkbox, WhiteSpace } from "@privacy/ui";
import { useTranslation } from "@privacy/translations";
import { Grid } from "./Grid";
import { Drawer } from "./Drawer";

const DIALOG_PADDING_X = 6;

/**
 * Shared "Replace & Archive" dialog: a selectable grid of an entity's connections with a
 * search + "replace selected" toolbar, and an Archive action that stays disabled until the
 * entity has no blocking connections left. Used by Partner and System; reuse it for any future
 * entity that must repoint its connections before archiving. The entity supplies the rows,
 * columns, the per-selection replace dialog, and the archive handler.
 */
type ReplaceAndArchiveDialogProps<Row extends GridValidRowModel> = {
  onClose: () => void;
  isLoading: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Rendered under the subtitle (e.g. a permission warning). */
  headerSlot?: ReactNode;
  rows: Array<Row>;
  getRowId: (row: Row) => string;
  /** Entity columns; the checkbox-selection column is prepended automatically. */
  columns: Array<GridColDef<Row>>;
  searchPlaceholder: string;
  replaceSelected: {
    label: ReactNode;
    renderDialog: (args: { selectedRows: Array<Row>; onClose: () => void }) => ReactNode;
  };
  archive: { label: ReactNode; isDisabled: boolean; isPending: boolean; onArchive: () => void };
  /** Rendered left of the Cancel/Archive buttons (e.g. a permission hint). */
  actionsSlot?: ReactNode;
  emptyState: ReactNode;
};

type ToolbarConfig = {
  searchPlaceholder: string;
  replaceLabel: ReactNode;
  renderReplaceDialog: (args: { selectedRows: Array<GridValidRowModel>; onClose: () => void }) => ReactNode;
};
const ToolbarConfigContext = createContext<ToolbarConfig | null>(null);

export const ReplaceAndArchiveDialog = <Row extends GridValidRowModel>({
  onClose,
  isLoading,
  title,
  subtitle,
  headerSlot,
  rows,
  getRowId,
  columns,
  searchPlaceholder,
  replaceSelected,
  archive,
  actionsSlot,
  emptyState,
}: ReplaceAndArchiveDialogProps<Row>) => {
  const { t } = useTranslation();
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>(() => ({
    type: "include",
    ids: new Set(),
  }));

  const toolbarConfig: ToolbarConfig = {
    searchPlaceholder,
    replaceLabel: replaceSelected.label,
    // The toolbar lives in a grid slot (can't be generic). Row identity is restored by the
    // caller's renderDialog, which is typed for the concrete Row.
    renderReplaceDialog: replaceSelected.renderDialog as ToolbarConfig["renderReplaceDialog"],
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{ paper: { sx: { px: 0, height: "min(95vh,1200px)" } } }}
    >
      {isLoading ? (
        <Drawer.Loading />
      ) : (
        <>
          <DialogTitle sx={{ px: DIALOG_PADDING_X }}>
            {title}
            {subtitle && (
              <Typography
                sx={{
                  mt: 1,
                  fontSize: theme => theme.typography.pxToRem(14),
                  color: theme => theme.palette.text.tertiary,
                }}
              >
                {subtitle}
              </Typography>
            )}
            {headerSlot}
          </DialogTitle>

          <DialogContent sx={{ px: 0, pb: "12px !important" }}>
            <ToolbarConfigContext value={toolbarConfig}>
              <Grid
                getRowHeight={() => "auto"}
                disableColumnMenu
                columnHeaderHeight={35}
                disableColumnReorder
                disableAutosize
                disableColumnResize
                disableRowSelectionOnClick
                hideFooter
                sortingOrder={["desc", "asc"]}
                showToolbar
                initialState={{ sorting: { sortModel: [{ field: "name", sort: "asc" }] } }}
                checkboxSelection
                disableRowSelectionExcludeModel
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={setRowSelectionModel}
                getRowId={getRowId}
                rows={rows}
                slots={{
                  toolbar: ReplaceAndArchiveToolbar,
                  baseCheckbox: CustomCheckbox,
                  noRowsOverlay: () => <>{emptyState}</>,
                  noResultsOverlay: () => <>{emptyState}</>,
                }}
                columns={[{ ...GRID_CHECKBOX_SELECTION_COL_DEF, width: 85 } as GridColDef<Row>, ...columns]}
              />
            </ToolbarConfigContext>
          </DialogContent>

          <DialogActions sx={{ px: DIALOG_PADDING_X }}>
            {actionsSlot}
            <Button variant="outlined" onClick={onClose}>
              {t("FormDialog.CancelButton")}
            </Button>
            <Button disabled={archive.isDisabled} loading={archive.isPending} onClick={archive.onArchive}>
              {archive.label}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

const ReplaceAndArchiveToolbar = () => {
  const config = use(ToolbarConfigContext);
  if (!config) throw new Error("ReplaceAndArchiveToolbar must render inside ReplaceAndArchiveDialog");

  const apiRef = useGridApiContext();
  const selectedRowsMap = useGridSelector(apiRef, gridRowSelectionIdsSelector);
  const [isReplaceOpen, setIsReplaceOpen] = useState(false);

  const selectedCount = selectedRowsMap.size;
  const selectedRows = Array.from(selectedRowsMap.values()).filter(Boolean);

  return (
    <Stack
      direction="row"
      sx={{ gap: 3, alignItems: "flex-end", justifyContent: "space-between", px: DIALOG_PADDING_X, py: 2 }}
    >
      <Box>
        <Grid.SearchField gridApiRef={apiRef} placeholder={config.searchPlaceholder} />
      </Box>
      <Button
        variant="text"
        size="small"
        sx={{ color: theme => theme.palette.primary["600"] }}
        disabled={selectedCount === 0}
        onClick={() => setIsReplaceOpen(true)}
      >
        {config.replaceLabel}
        <WhiteSpace />
        {selectedCount > 0 ? `(${selectedCount})` : ""}
      </Button>
      {isReplaceOpen && config.renderReplaceDialog({ selectedRows, onClose: () => setIsReplaceOpen(false) })}
    </Stack>
  );
};

type BaseCheckboxSlot = NonNullable<NonNullable<DataGridPremiumProps["slots"]>["baseCheckbox"]>;
const CustomCheckbox: BaseCheckboxSlot = props => {
  const { slotProps, ...checkboxProps } = props;
  return <Checkbox {...checkboxProps} size="small" indeterminate={false} slotProps={{ input: slotProps?.htmlInput }} />;
};
